import { Component, OnInit, Input } from '@angular/core';
import type { EChartsOption } from 'echarts';
import { catchError, debounceTime, Subscription, throwError } from 'rxjs';
import { SyncService } from 'src/app/services/sync-service';
import { FieldDashboardDataService } from '../field-dashboard-data.service';
import { formatDate } from '@angular/common';
import { CropBioKMaxData } from 'src/app/models/crop-bio-k-max-data';

@Component({
  selector: 'app-crop-bio',
  templateUrl: './crop-bio.component.html',
  styleUrls: ['./crop-bio.component.css'],
  standalone: false
})
export class CropBioComponent implements OnInit {
  @Input() fieldId: string = '';
  @Input() farmId: string;
  @Input() fromDate: String;
  @Input() toDate: String;

  chartOption: EChartsOption = {};
  hasData: boolean = true;
  isLoading: boolean = true;

  cropBioKMaxData: CropBioKMaxData = {
    cultivars: [],
    legends: [],
    cropBio: [],
    kMax: []
  };

  private subscription: Subscription;
  public series: any[] = [];

  color = ['#FFB400', '#6355C7', '#5070dd', '#b6d634', '#505372', '#ff994d', '#0ca8df'];
  cropBioColors = ['#1b5e20', '#2e7d32', '#388e3c', '#43a047', '#4caf50', '#66bb6a', '#81c784'];
  kMaxColors = ['#b71c1c', '#c62828', '#d32f2f', '#e53935', '#f44336', '#ef5350', '#e57373'];

  constructor(
    private syncService: SyncService,
    private dataService: FieldDashboardDataService
  ) {}

  ngOnInit(): void {
    this.subscription = this.dataService.fieldData$.pipe(debounceTime(100)).subscribe(data => {
      if (data.fieldId && data.farmId && data.startDate && data.endDate) {
        let fromDate: string;
        let toDate: string;

        if (data.userChangedDates) {
          fromDate = formatDate(data.startDate, 'yyyy-MM-dd', 'en-ZA');
          toDate = formatDate(data.endDate, 'yyyy-MM-dd', 'en-ZA');
        } else if (data.season) {
          const startYear = parseInt(data.season.split('/')[0]);
          fromDate = `${startYear}-06-01`;
          toDate = `${startYear + 1}-05-31`;
        } else {
          return;
        }

        if (this.fieldId === data.fieldId && this.fromDate === fromDate && this.toDate === toDate) return;

        this.fieldId = data.fieldId;
        this.farmId = '2475';
        this.fromDate = fromDate;
        this.toDate = toDate;
        this.populateData();
      }
    });
  }

  populateData() {
    this.isLoading = true;
    this.hasData = true;
    this.syncService.getCropBioKMaxStats(this.fieldId, this.fromDate, this.toDate).pipe(
      catchError(error => {
        console.error();
        return throwError(() => error);
      })
    ).subscribe((response) => {
      this.cropBioKMaxData = response;
      this.isLoading = false;
      this.loadChartData();
    });
  }

  loadChartData(): void {
    this.hasData = this.cropBioKMaxData.cultivars.length > 0 &&
      (this.cropBioKMaxData.kMax.some(v => v !== 0) || this.cropBioKMaxData.cropBio.some(v => v !== 0));
    if (!this.hasData) return;

    const weeks: string[] = [];
    const cropBioData: number[] = [];
    const kMaxData: number[] = [];

    for (let i = 0; i < this.cropBioKMaxData.legends.length; i++) {
      const cropVal = this.cropBioKMaxData.cropBio[i];
      const kMaxVal = this.cropBioKMaxData.kMax[i];
      if (cropVal === 0 && kMaxVal === 0) continue;
      weeks.push('Week ' + this.cropBioKMaxData.legends[i]);
      cropBioData.push(cropVal);
      kMaxData.push(kMaxVal);
    }

    this.series = [];
    let cropBioIdx = 0;
    let kMaxIdx = 0;
    weeks.forEach((week, idx) => {
      const hasCropBio = cropBioData[idx] !== 0;
      const hasKMax = kMaxData[idx] !== 0;
      const legendColor = hasCropBio
        ? this.cropBioColors[cropBioIdx % this.cropBioColors.length]
        : this.kMaxColors[kMaxIdx % this.kMaxColors.length];

      this.series.push({
        name: week,
        type: 'bar' as const,
        stack: 'cropBio',
        yAxisIndex: 0,
        data: [cropBioData[idx], 0],
        label: { show: true, position: 'inside' as const, formatter: (p) => p.value !== 0 ? p.value.toString() : '' },
        itemStyle: { color: hasCropBio ? this.cropBioColors[cropBioIdx % this.cropBioColors.length] : legendColor },
        barWidth: '40%',
        barGap: '-100%'
      });
      if (hasCropBio) cropBioIdx++;
      this.series.push({
        name: week,
        type: 'bar' as const,
        stack: 'kMax',
        yAxisIndex: 1,
        data: [0, kMaxData[idx]],
        label: { show: true, position: 'inside' as const, formatter: (p) => p.value !== 0 ? p.value.toString() : '' },
        itemStyle: { color: hasKMax ? this.kMaxColors[kMaxIdx % this.kMaxColors.length] : 'transparent' },
        barWidth: '40%',
        barGap: '-100%'
      });
      if (hasKMax) kMaxIdx++;
    });

    this.chartOption = {
      title: {
        text: 'CropBio & K-Max/Block',
        left: 0,
        top: 10,
        textStyle: { fontSize: 18, fontWeight: 'bold' }
      },
      tooltip: {
        trigger: 'item',
        formatter: (params: any) => params.value !== 0 ? `${params.seriesName}: ${params.value}` : ''
      },
      legend: {
        bottom: 0,
        itemGap: 25,
        type: 'scroll'
      },
      grid: {
        left: '10%',
        right: '10%',
        bottom: '10%',
        top: '20%',
        containLabel: true
      },
      xAxis: {
        type: 'category',
        data: ['CropBioLife', 'K-Max']
      },
      yAxis: [
        {
          type: 'value',
          name: 'CropBio (L/Ha)',
          nameLocation: 'middle',
          axisLabel: { formatter: '{value}' },
          nameGap: 40,
          nameTextStyle: { fontWeight: 'bold' }
        },
        {
          type: 'value',
          name: 'K-Max (L/Ha)',
          nameLocation: 'middle',
          axisLabel: { formatter: '{value}' },
          nameGap: 40,
          nameTextStyle: { fontWeight: 'bold' }
        }
      ],
      series: this.series
    };
  }
}
